// src/pages/documentation/quick-start/QuickStartRegistro.jsx

import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEnvelope,
    faLock,
    faUser,
    faGlobe,
    faCheckCircle,
    faArrowRight,
    faArrowLeft,
    faExclamationTriangle,
    faInfoCircle,
    faClock,
    faRocket
} from '@fortawesome/free-solid-svg-icons';
import DocumentationLayout from '../../../components/documentation/DocumentationLayout';

const QuickStartRegistro = () => {
    const registroSteps = [
        {
            step: 1,
            title: 'Accede a la página de registro',
            description: 'Ve a la página principal de PetitPrime y haz clic en "Crear cuenta" o "Registrarse"',
            details: 'Puedes encontrar el botón de registro en la esquina superior derecha de la página principal o directamente en /register'
        },
        {
            step: 2,
            title: 'Completa tus datos personales',
            description: 'Llena el formulario con tu información: nombre, apellido, email y contraseña',
            details: 'Asegúrate de usar un email que revises frecuentemente, ya que recibirás información importante allí.'
        },
        {
            step: 3,
            title: 'Selecciona tu país',
            description: 'Elige tu país: España, Perú o Chile',
            details: 'Esta información ayuda a personalizar la experiencia según las características de tu mercado local.'
        },
        {
            step: 4,
            title: 'Acepta los términos y condiciones',
            description: 'Lee y acepta los términos de servicio y política de privacidad',
            details: 'Es importante que conozcas tus derechos y nuestras políticas antes de continuar.'
        },
        {
            step: 5,
            title: 'Crea tu cuenta',
            description: 'Haz clic en "Crear cuenta" y espera el email de confirmación',
            details: 'El proceso de registro es instantáneo. Recibirás un email de verificación en los próximos minutos.'
        }
    ];

    return (
        <DocumentationLayout>
            <div className="p-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-3 bg-blue-600 rounded-lg">
                            <FontAwesomeIcon icon={faUser} className="text-white text-2xl" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-800">Paso 1: Crear tu cuenta</h1>
                            <p className="text-gray-600">Regístrate en PetitPrime con tu email</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Link to="/documentation" className="hover:text-gray-700">Centro de Ayuda</Link>
                        <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                        <Link to="/documentation/quick-start" className="hover:text-gray-700">Inicio Rápido</Link>
                        <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                        <span className="text-gray-700">Registro</span>
                    </div>
                </div>

                {/* Overview */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 mb-8">
                    <div className="flex items-start gap-4">
                        <FontAwesomeIcon icon={faInfoCircle} className="text-blue-600 text-xl mt-1" />
                        <div>
                            <h3 className="font-semibold text-gray-900 mb-2">¿Qué vas a hacer?</h3>
                            <p className="text-gray-700 mb-3">
                                En este paso crearás tu cuenta en PetitPrime. Necesitarás un email válido y crear una contraseña segura.
                            </p>
                            <div className="flex items-center gap-4 text-sm text-gray-600">
                                <span className="flex items-center gap-2">
                                    <FontAwesomeIcon icon={faClock} />
                                    Tiempo estimado: 3 minutos
                                </span>
                                <span className="flex items-center gap-2">
                                    <FontAwesomeIcon icon={faCheckCircle} />
                                    5 pasos simples
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Steps */}
                <div className="space-y-4 mb-8">
                    {registroSteps.map((step) => (
                        <div key={step.step} className="bg-white border border-gray-200 rounded-lg p-6">
                            <div className="flex items-start gap-4">
                                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold">
                                    {step.step}
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-2">{step.title}</h3>
                                    <p className="text-gray-600 mb-2">{step.description}</p>
                                    <p className="text-gray-500 text-sm italic">{step.details}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Important Notes */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
                    <h4 className="font-semibold text-yellow-800 mb-3 flex items-center gap-2">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        Importante sobre tu contraseña
                    </h4>
                    <ul className="text-yellow-700 space-y-2">
                        <li className="flex items-start gap-2">
                            <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                            <span>Usa al menos 6 caracteres</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                            <span>Combina letras y números para mayor seguridad</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                            <span>No compartas tu contraseña con nadie</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                            <span>Si la olvidas, puedes recuperarla desde "¿Olvidaste tu contraseña?"</span>
                        </li>
                    </ul>
                </div>

                {/* What happens next */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
                    <h4 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                        <FontAwesomeIcon icon={faCheckCircle} />
                        ¿Qué pasa después de registrarte?
                    </h4>
                    <p className="text-green-700 mb-3">
                        Inmediatamente después de crear tu cuenta, recibirás un email de verificación en la dirección que proporcionaste.
                    </p>
                    <div className="bg-white rounded-lg p-4 border border-green-100">
                        <p className="text-green-800 font-medium mb-2">⚠️ Importante:</p>
                        <p className="text-green-700 text-sm">
                            <strong>No podrás iniciar sesión hasta que verifiques tu email</strong>. 
                            Asegúrate de revisar tu bandeja de entrada (y la carpeta de spam si no lo encuentras).
                        </p>
                        <p className="text-green-700 text-sm mt-2">
                            El email puede tardar unos minutos en llegar. Si después de 5 minutos no lo has recibido, 
                            puedes solicitar que se reenvíe desde la página de inicio de sesión.
                        </p>
                    </div>
                </div>

                {/* Navigation */}
                <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                        <FontAwesomeIcon icon={faArrowLeft} />
                        <span>No hay página anterior</span>
                    </div>
                    <Link
                        to="/documentation/quick-start/login"
                        className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                    >
                        Siguiente: Iniciar Sesión
                        <FontAwesomeIcon icon={faArrowRight} />
                    </Link>
                </div>
            </div>
        </DocumentationLayout>
    );
};

export default QuickStartRegistro;

